/**
 * Typed routes map — typed routes map for Next.js navigation.
 *
 * Each route is a function returning a RouteUrlObject compatible
 * with Next.js Link and router.push().
 */
import type { RouteUrlObject } from "app/auth/types"

type Params = Record<string, string | number | boolean | undefined | null>

function route(pathname: string, query?: Params): RouteUrlObject {
  const cleanQuery: Record<string, string> = {}
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== undefined) cleanQuery[k] = String(v)
    }
  }
  const hasQuery = Object.keys(cleanQuery).length > 0
  const result = {
    pathname,
    query: hasQuery ? cleanQuery : undefined,
  } as RouteUrlObject
  // Non-enumerable toString so url.format() doesn't warn about unknown keys
  Object.defineProperty(result, "toString", {
    enumerable: false,
    value() {
      let url = pathname
      if (hasQuery) {
        const remainingQuery: Record<string, string> = {}
        for (const [k, v] of Object.entries(cleanQuery)) {
          if (url.includes(`[${k}]`)) {
            url = url.replace(`[${k}]`, encodeURIComponent(v))
          } else {
            remainingQuery[k] = v
          }
        }
        const qs = new URLSearchParams(remainingQuery).toString()
        if (qs) url += `?${qs}`
      }
      return url
    },
  })
  return result
}

export const Routes = {
  // Auth
  Home: () => route("/"),
  LoginPage: (q?: { next?: string }) => route("/login", q),
  ForgotPasswordPage: () => route("/forgot-password"),
  JoinPage: (q: { token?: string }) => route("/join", q),

  // Dashboards
  DashboardViewer: (q: {
    dashboardId: string
    notificationTitle?: string
    notificationBody?: string
    [key: string]: string | undefined
  }) => route("/dashboard/[dashboardId]", q),
  DashboardDesigner: (q: { dashboardId: string; btn?: string }) =>
    route("/design/[dashboardId]", q),
  DashboardUploader: (q: { dashboardId: string; btn?: string }) =>
    route("/upload/[dashboardId]", q),
  InstaDashboardViewer: (q: { dashboardId: string }) => route("/insta/[dashboardId]", q),

  // Teams (Profiles)
  MyTeamsPage: () => route("/team"),
  TeamSettingsPage: (q: { teamId: string }) => route("/team/[teamId]/settings", q),
  TeamLibraryPage: (q: { teamId: string; tab?: string }) => route("/team/[teamId]/library", q),
  TeamRecruitmentPage: (q: { teamId: string }) => route("/team/[teamId]/recruitment", q),
  TeamMessagePage: (q: { teamId: string; tab?: string; backTo?: string }) =>
    route("/team/[teamId]/message", q),
  InviteToTeamPage: (q: { teamId: string }) => route("/team/[teamId]/invite", q),
  RedirectToFirstDashboardPage: (q: { profileId: string }) => route("/profile/[profileId]", q),
  NewProfilePage: (q?: { groupId?: string; public?: string | boolean; next?: string }) =>
    route("/profile/new", q as Params),

  // Groups
  GroupSettingsPage: (q: { groupId: string; tab?: string }) =>
    route("/group/[groupId]/settings", q),
  EditGroupSettingsPage: (q: { groupId: string }) => route("/group/[groupId]/edit", q),
  NewGroupPage: (q?: { organisationId?: string; next?: string }) => route("/group/new", q),

  // Organisations
  OrganisationSettingsPage: (q: { organisationId: string }) =>
    route("/organisation/[organisationId]/settings", q),
  NewOrganisationPage: (q?: { next?: string }) => route("/admin/organisation/new", q),

  // League Exchanges
  LeagueExchangePage: (q: { leagueExchangeId: string }) =>
    route("/league-exchange/[leagueExchangeId]", q),
  LeagueExchangeSettingsPage: (q: { leagueExchangeId: string }) =>
    route("/league-exchange/[leagueExchangeId]/settings", q),
  InviteToLeagueExchangePage: (q: { leagueExchangeId: string }) =>
    route("/league-exchange/[leagueExchangeId]/invite", q),
  InviteToLeagueExchangeTeamPage: (q: { leagueExchangeId: string; leagueExchangeTeamId: string }) =>
    route("/league-exchange/[leagueExchangeId]/team/[leagueExchangeTeamId]/invite", q),
  LeagueExchangeTeamSettingsPage: (q: { leagueExchangeId: string; leagueExchangeTeamId: string }) =>
    route("/league-exchange/[leagueExchangeId]/team/[leagueExchangeTeamId]/settings", q),
  NewLeagueExchangePage: (q?: { next?: string }) => route("/admin/league-exchange/new", q),

  // Media
  MediaPage: (q: { mediaId: string; token?: string; filename?: string }) =>
    route("/media/[mediaId]", q),
  AccountSettings: () => route("/settings/account"),

  // Recruitment
  RecruitmentPage: (q: {
    recruitmentId: string
    slug: string
    dashboardId?: string
    [key: string]: string | undefined
  }) => route("/id/[recruitmentId]/[slug]", q),

  // Admin
  AdminPage: () => route("/admin"),
  UsersAdminPage: () => route("/admin/user"),
  UsersSupportPage: () => route("/support/user"),
  TeamsAdminPage: (q?: { public?: string | boolean }) => route("/admin/team", q as Params),
  TeamAdminPage: (q: { teamId: string }) => route("/admin/team/[teamId]", q),
  NewTeamAdminPage: (q?: { public?: string | boolean }) => route("/admin/team/new", q as Params),
  OrganisationsAdminPage: () => route("/admin/organisation"),
  OrganisationAdminPage: (q: { organisationId: string }) =>
    route("/admin/organisation/[organisationId]", q),
  EditOrganisationAdminPage: (q: { organisationId: string }) =>
    route("/admin/organisation/[organisationId]/edit", q),
  GroupAdminPage: (q: { groupId: string }) => route("/admin/group/[groupId]", q),
  LeagueExchangesAdminPage: () => route("/admin/league-exchange"),
  LeagueExchangeAdminPage: (q: { leagueExchangeId: string }) =>
    route("/admin/league-exchange/[leagueExchangeId]", q),
  UnprocessedVideosAdminPage: () => route("/admin/video-migration"),
}
